<template>
  <spinner v-if="showSpinner" fullScreen />
  <DialogComponent 
    v-else 
    @submit="onSubmit"
    :title="(campaign.campaignName) ? campaign.campaignName : 'Unknown Name'"
  >
    <div class="alert alert-danger" v-show="!campaign.status">The campaign is disabled</div>
    <form @submit="onSubmit">
      <b-form-group
        id="campaignName"
        label="Campaign name:"
        label-for="campaignName"
      >
        <input
          id="campaignName"
          type="text"
          :class="{'is-invalid': errors.has('campaignName')}"
          v-model="campaign.campaignName"
          placeholder="Enter campaign name"
          v-validate="'required'"
          name="campaignName"
          data-vv-as="Campaign Name"
          class="form-control"
          autofocus
        />
        <span
          v-show="errors.has('campaignName')"
          class="error-message"
        >
          {{ errors.first('campaignName') }}
        </span>
      </b-form-group>
      <b-form-group
        label="Company Name:"
        label-for="companyName"
      >
        <multiselect
          id="companyName"
          :disabled="isClient"
          :options="companies"
          fieldName="companyName"
          v-model="campaign.company"
          :defaultValue="campaign.company"
          v-validate="'required'"
          name="company"
          data-vv-as="Company"
          :isValid="!errors.has('company')"
          class="form-control"
        ></multiselect>
        <span
          v-show="errors.has('company')"
          class="error-message"
        >
          {{ errors.first('company') }}
        </span>
      </b-form-group>
      <div class="row" v-if="!isClient">
        <div class="col-md-4">
          <b-form-group
            id="status"
            label="Status:"
            label-for="status"
          >
            <b-form-select id="status" v-model="campaign.status" :options="optionStatus"></b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group
            id="editable"
            label="Editable:"
            label-for="editable">
            <b-form-select id="editable" v-model="campaign.editable" :options="optionEditable"></b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group
            id="whitelabel"
            label="White-Label:"
            label-for="whitelabel">
            <b-form-select id="whitelabel" v-model="campaign.whiteLabel" :options="optionWhitelabel"></b-form-select>
          </b-form-group>
        </div>
      </div>
    </form>
  </DialogComponent>
</template>

<script>
import Switches from 'vue-switches';
import { mapGetters } from 'vuex';

import Multiselect from '@/components/CustomMultiselect';
import DialogComponent from '@/components/Dialog';
import Spinner from '@/components/Spinner';

import CAMPAIGN from '@/graphql/Campaigns/Campaign.gql';
import ADMIN_COMPANIES from '@/graphql/Companies/Companies.gql';
import CLIENT_COMPANY from '@/graphql/Companies/Company.gql';

import CREATE_CAMPAIGN from '@/graphql/Campaigns/CreateCampaign.gql';
import EDIT_CAMPAIGN from '@/graphql/Campaigns/EditCampaign.gql';
import ModalMixin from '@/mixins/ModalMixin';
import checkIsAccessDenied from '@/helpers/checkIsAccessDenied';

export default {
  name: 'edit-campaign-component',
  mixins: [ModalMixin],
  components: {
    Switches,
    DialogComponent,
    Multiselect,
    Spinner,
  },
  props: {
    id: String,
  },
  async created() {
    const { data } = await this.$apollo.query({
      query: this.isClient ? CLIENT_COMPANY : ADMIN_COMPANIES,
      variables: this.isClient ? { id: this.getAuthUser.companyId } : { limit: -1 },
    });
    this.companies = this.isClient ? [data.company] : data.companies.result;
    if (this.isClient) {
      this.campaign.company = data.company;
    }
  },
  data() {
    return {
      companies: [],
      campaign: {
        campaignName: '',
        dispatchTo: '',
        dispatchToPhone: '',
        company: null,
        status: true,
        editable: false,
        whiteLabel: false,
        type: 'shopify'
      },
      showSpinner: 0,
      optionStatus: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      optionEditable: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      optionWhitelabel: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ]
    };
  },
  computed: {
    ...mapGetters(['getAuthUser']),

    isClient() {
      return this.getAuthUser.role.role_type === 'client';
    },

    title() {
      return `${this.id ? 'Update' : 'Create'} Campaign`;
    },
  },
  methods: {
    async onSubmit() {
      try {
        const valid = await this.$validator.validateAll();
        if (!valid) {
          return;
        }
      } catch (e) {
        return;
      }
      const { campaignName, dispatchTo, dispatchToPhone, company, status, editable, whiteLabel, type } = this.campaign;

      this.$apollo
        .mutate({
          mutation: this.id ? EDIT_CAMPAIGN : CREATE_CAMPAIGN,
          variables: {
            id: parseInt(this.id),
            input: {
              campaignName,
              dispatchTo,
              dispatchToPhone,
              companyId: parseInt(company.id),
              status,
              editable,
              whiteLabel,
              type
            },
          },
          refetchQueries: ({ data }) => {
            const response = data.createCampaign || data.updateCampaign;
            return response.success ? ['campaigns'] : [];
          },
        })
        .then(() => {
          this.$notify({
            group: 'success',
            type: 'success',
            text: `Campaign was ${this.id ? 'edited' : 'created'}`,
          });
          this.close();
        })
        .catch(e => {
          const isAccessDenied = checkIsAccessDenied(e);

          if (e.message.includes('ER_DUP_ENTRY')) {
            return this.$validator.errors.add('campaignName', 'This Campaign Name already exists');
          }

          if (isAccessDenied) {
            return this.$notify({
              group: 'error',
              type: 'error',
              text: 'Access denied',
            });
          }

          this.$notify({
            group: 'error',
            type: 'error',
            text: e.message,
          });
        });
    },
  },
  apollo: {
    campaign: {
      query: CAMPAIGN,
      loadingKey: 'showSpinner',
      fetchPolicy: 'network-only',
      variables() {
        return {
          id: +this.id,
        };
      },
      result({ data, loading }) {
        if (!loading) {
          this.campaign = {
            campaignName: data.campaign.campaignName,
            dispatchTo: data.campaign.dispatchTo,
            dispatchToPhone: data.campaign.dispatchToPhone,
            company: data.campaign.company,
            status: data.campaign.status,
            editable: data.campaign.editable,
            whiteLabel: data.campaign.whiteLabel,
          };
        }
      },
      skip() {
        return !this.id;
      },
      error(error) {
        const isAccessDenied = checkIsAccessDenied(error);

        if (isAccessDenied) {
          this.$router.push({ name: 'NotFound' });
        }
      },
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.checkboxes-wrapper {
  max-height: 378px;
  overflow: auto;

  .form-check {
    border-top: 1px solid #c1c1c1;
    padding-top: 0.4rem;
    margin-bottom: 0.4rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/modals/EditShopifyCampaignModal.vue